import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import cx from 'classnames';

import {
    dateRangeToFilterInput,
    locale,
    getWebpackEnvVar,
    getTimestampFilterInputFromURLParams,
    updateAndReplaceURLSearchParams,
    timestampFilterToISODateRange,
} from '@trqk/utils';
import {
    Button,
    IconMusicFile,
    leftNavConstants,
    LeftNavHeader,
    LoadingSpinner,
    PreFilters,
    URLParamDateRangeQueryFilter,
} from '@trqk/components';
import {
    useAutoSize,
    useDetectionsTableItems,
    useStateWithCallback,
} from '@trqk/hooks';

import { WebLayout } from '../../templates';
import { DetectionsEmptyState, DetectionsTable } from './components';
import { ManageDetections } from '../../modals';
import {
    useEntities,
    useModal,
    useTokenModal,
    useToast,
    useDetectionTracks,
    usePaymentPlanStatus,
} from '../../../hooks';
import { ManageDetectionsLink } from '../../nav';
import { FULL_PATHS, URL_PARAMS } from '../../../constants';
import { defaultDateRangeOption, preFilterConfigs } from './constants';

const { AUTO_SIZE_OFFSETS, HEIGHTS, WIDTHS } = leftNavConstants;

const preFilterURLParamKeys = Array.from(preFilterConfigs.values()).map(
    ({ urlParamKey }: PreFilterConfig): string => urlParamKey,
);

const allURLParamKeys = [
    ...preFilterURLParamKeys,
    URL_PARAMS.DATE_START,
    URL_PARAMS.DATE_END,
    URL_PARAMS.DATE_LABEL,
];

const nullURLParams = allURLParamKeys.map(
    (key: string): URLSearchParam => ({
        key,
        value: null,
    }),
);

export const Detections = (): JSX.Element => {
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { sendEntitySelectionEvent, setStateListeners } = useEntities();
    const history = useHistory();

    // If we are on the `/detections/control-panel` route, open the ManageDetections modal;
    // if we are on the `/detections` route, close the ManageDetections modal
    const modal = useModal();
    const { add: addToast } = useToast();
    const { getToken } = useTokenModal();
    const match = useRouteMatch({
        path: FULL_PATHS.MANAGE_DETECTIONS,
        strict: true,
        sensitive: true,
    });
    const [detectionsControlPanelIsOpen, setManageDetectionsIsOpen] =
        useState(false);

    const onSetFilterInput = useCallback(
        (
            updatedFilterInput: Record<
                string,
                Maybe<TableStringFilterInput | TableIntFilterInput>
            >,
        ) => {
            const timestampFilter = updatedFilterInput?.timestamp ?? null;

            if (timestampFilter !== null) {
                const isoRange = timestampFilterToISODateRange(timestampFilter);

                if (isoRange !== null) {
                    const start =
                        isoRange.start === null
                            ? null
                            : new Date(isoRange.start);
                    const end =
                        isoRange.end === null ? null : new Date(isoRange.end);

                    updateAndReplaceURLSearchParams(history, [
                        {
                            key: URL_PARAMS.DATE_START,
                            value: start === null ? start : start.toISOString(),
                        },
                        {
                            key: URL_PARAMS.DATE_END,
                            value: end === null ? end : end.toISOString(),
                        },
                    ]);
                }
            }
        },
        [],
    );

    const [filterInput, setFilterInput] = useStateWithCallback<
        Record<string, Maybe<TableStringFilterInput | TableIntFilterInput>>
    >(
        getTimestampFilterInputFromURLParams({
            defaultValue: {
                timestamp: dateRangeToFilterInput(defaultDateRangeOption.value),
            },
            search: history.location.search,
            urlParamKeys: {
                end: URL_PARAMS.DATE_END,
                start: URL_PARAMS.DATE_START,
            },
        }),
        onSetFilterInput,
    );

    const {
        detectionsTableItems,
        detectionTotals,
        fetchAllItems,
        loadingDetectionsTableItems,
        loadingDetectionTotals,
        preFilterState,
        sendPreFilterEvent,
    } = useDetectionsTableItems({
        addToast,
        filterInput,
        getToken,
        preFilterConfigs,
        trqkDirectoryUrl,
    });
    const { detectionTracks } = useDetectionTracks();
    const userHasNoAudioFiles = detectionTracks.length === 0;

    useEffect((): void => {
        if (match !== null && !detectionsControlPanelIsOpen) {
            setManageDetectionsIsOpen(true);

            modal.add({
                Content: ManageDetections,
                handleClose: (): void => {
                    // Re-fetch detections in case detection data has changed. This would happen if
                    // an audio file was deleted by the user or more detections were added to the
                    // database while the modal was open.
                    fetchAllItems([filterInput]);
                },
            });
        }

        if (match === null && detectionsControlPanelIsOpen) {
            setManageDetectionsIsOpen(false);
        }
    }, [
        detectionsControlPanelIsOpen,
        filterInput,
        match,
        modal,
        sendEntitySelectionEvent,
    ]);

    // Ensure that entity selection is set to "View All" for this page and disable entity selection
    useEffect(() => {
        sendEntitySelectionEvent({
            type: 'SET_DISABLE_SELECTION',
            value: {
                disableSelection: true,
            },
        });

        sendEntitySelectionEvent({
            type: 'SET_VIEW_ALL',
            value: {
                viewAll: true,
            },
        });

        return (): void => {
            sendEntitySelectionEvent({
                type: 'SET_DISABLE_SELECTION',
                value: {
                    disableSelection: false,
                },
            });

            sendEntitySelectionEvent({
                type: 'SET_VIEW_ALL',
                value: {
                    viewAll: false,
                },
            });
        };
    }, [sendEntitySelectionEvent]);

    const { showFreeTrialFooter } = usePaymentPlanStatus();

    const tableElementRef = useRef<HTMLDivElement>(null);

    const { height, width, getSize } = useAutoSize({
        elementRef: tableElementRef,
        resizeRate: 400,
        rightOffset: 8,
        bottomOffset: showFreeTrialFooter
            ? HEIGHTS.ACCOUNT_DROPDOWN + AUTO_SIZE_OFFSETS.BOTTOM
            : AUTO_SIZE_OFFSETS.BOTTOM,
        minWidth: WIDTHS.MAIN_XL - AUTO_SIZE_OFFSETS.MIN_WIDTH,
    });

    const mainWidth = Math.max(WIDTHS.MAIN_XL, width);
    const tableWidth = mainWidth - 50;

    const [loadingPreFilters, setLoadingPreFilters] = useState(true);

    useEffect(() => {
        // It takes some time to paint the filters and table on the screen once the data is
        // ready within the preFilter state machine. This gives the browser some time to do that
        // work before we un-hide the table and filters.
        if (preFilterState.value === 'ready') {
            setTimeout(() => {
                setLoadingPreFilters(false);
            }, 500);
            setTimeout(getSize, 1000);
            setTimeout(getSize, 2000);
        }
    }, [preFilterState.value]);

    useEffect(
        () => (): void => {
            // Delete URL params used by the Detections table filters when this page unmounts
            updateAndReplaceURLSearchParams(history, nullURLParams);
        },
        [],
    );

    return (
        <WebLayout
            mainWidth={null}
            headerPadding="xs"
            loading={loadingDetectionTotals}
            header={{
                content: (
                    <LeftNavHeader>
                        <div
                            className="flex items-start justify-between"
                            style={{ minWidth: 760 }}
                        >
                            <h3 data-cy="detections-title">
                                {locale.enUS.DETECTIONS.TITLE}
                            </h3>
                            <div
                                style={{
                                    marginRight: 360,
                                }}
                            >
                                <ManageDetectionsLink>
                                    <Button
                                        addClasses="respond-to-mouse"
                                        dataCy="manage-detections-top-button"
                                        color="primary-light-bg"
                                        padding="lg"
                                        autoWidth={true}
                                        boxShadow={true}
                                    >
                                        <div className="flex items-center justify-center mx-2 text-sm font-semibold">
                                            <IconMusicFile
                                                addClasses="mr-1"
                                                fillColor="tertiary-200"
                                            />
                                            {
                                                locale.enUS.DETECTIONS.BUTTONS
                                                    .MANAGE_DETECTIONS
                                            }
                                        </div>
                                    </Button>
                                </ManageDetectionsLink>
                            </div>
                        </div>
                        <div className="mt-3 text-sm">
                            {locale.enUS.DETECTIONS.DESCRIPTION.PART_1}
                            <br />
                            {locale.enUS.DETECTIONS.DESCRIPTION.PART_2}
                        </div>
                    </LeftNavHeader>
                ),
            }}
        >
            {!userHasNoAudioFiles && loadingPreFilters && (
                <div
                    className="flex items-center justify-center w-full h-full mt-32"
                    style={{ width: WIDTHS.MAIN_XL, height: 500 }}
                >
                    <LoadingSpinner />
                </div>
            )}
            {!userHasNoAudioFiles && (
                <div
                    className={cx(
                        'pt-6 mb-6 flex flex-col w-full h-full rounded-lg bg-white',
                        {
                            hidden: loadingPreFilters,
                        },
                    )}
                    style={{ width: mainWidth }}
                >
                    {preFilterState.value === 'ready' && (
                        <DetectionsTable
                            detections={detectionsTableItems ?? []}
                            loading={loadingDetectionsTableItems}
                            detectionTotals={detectionTotals}
                            height={height}
                            filterInput={filterInput}
                            filters={
                                <PreFilters<DetectionsTableItem>
                                    current={preFilterState}
                                    getSize={getSize}
                                    preFilterConfigs={preFilterConfigs}
                                    send={sendPreFilterEvent}
                                    setStateListeners={setStateListeners}
                                    showByDefault={true}
                                    tableName="detections"
                                    width={tableWidth}
                                    renderedFilters={
                                        <URLParamDateRangeQueryFilter<TableBmatDetectionFilterInput>
                                            defaultOption={
                                                defaultDateRangeOption
                                            }
                                            filterInput={filterInput}
                                            fieldName="timestamp"
                                            modal={modal}
                                            setFilterInput={setFilterInput}
                                            urlParams={{
                                                end: URL_PARAMS.DATE_END,
                                                label: URL_PARAMS.DATE_LABEL,
                                                start: URL_PARAMS.DATE_START,
                                            }}
                                        />
                                    }
                                />
                            }
                            getSize={getSize}
                            preFilterState={preFilterState}
                            ref={tableElementRef}
                            refreshTime={preFilterState.context.refreshTime}
                            width={tableWidth}
                        />
                    )}
                </div>
            )}

            {userHasNoAudioFiles && <DetectionsEmptyState />}
        </WebLayout>
    );
};
